import { FLOW_WINDOWS, type FlowRow, type FlowTable, type FlowWindow } from "../../shared/api.js";
import { Page } from "../Page.js";
import { favoriteStar, favoritesFirst } from "../components/Favorite.js";
import { rate, UnitToggle, windowLabel } from "../components/FlowBits.js";
import { openResource } from "../components/ResourceDialog.js";
import { stackIcon } from "../components/StackIcon.js";
import { debounce, h, patch } from "../dom.js";
import { amount, duration, full, stackName } from "../format.js";

const PAGE_SIZE = 100;

type Sort = "moved" | "in" | "out" | "net" | "name";

const SORTS: Record<Sort, { label: string; cmp: (a: FlowRow, b: FlowRow) => number }> = {
  moved: { label: "Most moved", cmp: (a, b) => b.in + b.out - (a.in + a.out) },
  in: { label: "Most produced", cmp: (a, b) => perMs(b.in, b) - perMs(a.in, a) },
  out: { label: "Most used", cmp: (a, b) => perMs(b.out, b) - perMs(a.out, a) },
  net: { label: "Falling fastest", cmp: (a, b) => perMs(a.in - a.out, a) - perMs(b.in - b.out, b) },
  name: { label: "Name", cmp: (a, b) => stackName(a.stack).localeCompare(stackName(b.stack)) },
};

function perMs(total: number, r: FlowRow): number {
  return r.observedMs > 0 ? total / r.observedMs : 0;
}

/**
 * Production and use of every watched resource, from each stock change the
 * Butler reported, as averages per second, minute, hour or day over a window.
 */
export class FlowsPage extends Page {
  readonly title = "Flows";
  private data: FlowTable | null = null;
  private shown = PAGE_SIZE;

  private readonly search = h("input", { type: "search", placeholder: "Search resources…" });
  private readonly type = h("select", null, h("option", { value: "" }, "All types"), h("option", { value: "item" }, "Items"), h("option", { value: "fluid" }, "Fluids"), h("option", { value: "essentia" }, "Essentia"));
  private readonly sort = h("select", null, ...Object.entries(SORTS).map(([v, s]) => h("option", { value: v }, `Sort: ${s.label.toLowerCase()}`)));
  private readonly window = h("select", { title: "Window the totals and averages cover" }, ...(Object.keys(FLOW_WINDOWS) as FlowWindow[]).map((w) => h("option", { value: w, selected: w === "24h" }, windowLabel(w))));
  private readonly idle = h("input", { type: "checkbox" });
  private readonly unit = new UnitToggle(() => this.render());
  private readonly summary = h("p", { class: "muted small" });
  private readonly list = h("div");

  mount(): void {
    const rerender = debounce(() => {
      this.shown = PAGE_SIZE;
      this.render();
    }, 150);
    this.search.addEventListener("input", rerender);
    this.type.addEventListener("change", rerender);
    this.sort.addEventListener("change", rerender);
    this.idle.addEventListener("change", rerender);
    this.window.addEventListener("change", () => void this.load());
    this.el.append(
      h("h2", null, "Production and use"),
      h(
        "p",
        { class: "muted small" },
        "Every change the Butler reports for a watched resource (a rule, a favorite or one you monitor) is recorded. In is the sum of every rise (crafts, auto-production, inserts), out the sum of every fall. " +
          "Averages divide by the time the app watched it live, so time with the game offline doesn't count. Click a row for its windows side by side and every change.",
      ),
      h(
        "div",
        { class: "toolbar" },
        this.search,
        this.type,
        this.sort,
        this.window,
        h("label", { class: "check small" }, this.idle, "Show unchanged"),
        h("span", { class: "spacer" }),
        this.unit.el,
      ),
      this.summary,
      this.list,
    );
    this.listen("favorites", () => this.render());
    this.every(30_000, () => void this.load());
    void this.load();
  }

  private async load(): Promise<void> {
    const data = await this.attempt(() => this.ctx.api.flows(this.window.value as FlowWindow));
    if (!data) return;
    this.data = data;
    this.render();
  }

  private render(): void {
    const all = this.data?.rows ?? [];
    const q = this.search.value.trim().toLowerCase();
    const type = this.type.value;
    const rows = all.filter(
      (r) =>
        (this.idle.checked || r.moves > 0) &&
        (!type || r.stack.type === type) &&
        (!q || `${stackName(r.stack)}\n${r.key}`.toLowerCase().includes(q)),
    );
    rows.sort(SORTS[this.sort.value as Sort].cmp);
    const favorites = this.ctx.live.favorites;
    const sorted = favoritesFirst(rows, (r) => favorites.has(r.key));
    const moved = all.filter((r) => r.moves > 0).length;
    this.summary.textContent = this.data
      ? `${moved} of ${all.length} watched resources changed in this window. Single changes are kept ${this.data.keptDays} day${this.data.keptDays === 1 ? "" : "s"} (Settings)` +
        (this.data.exact ? "." : ", so this window is read from hourly totals and starts on the hour.")
      : "Loading…";
    if (!sorted.length) {
      patch(this.list, h("p", { class: "muted empty" }, all.length ? "Nothing matches." : "No change recorded yet. Changes are recorded while the game is connected."));
      return;
    }
    const u = this.unit.unit;
    const shown = sorted.slice(0, this.shown);
    patch(this.list, 
      h(
        "div",
        { class: "table-wrap" },
        h(
          "table",
          { class: "flows" },
          h(
            "thead",
            null,
            h(
              "tr",
              null,
              h("th", null, "Resource"),
              h("th", { class: "num" }, "Stock"),
              h("th", { class: "num" }, `In /${u}`),
              h("th", { class: "num" }, `Out /${u}`),
              h("th", { class: "num" }, `Net /${u}`),
              h("th", { class: "num" }, "In"),
              h("th", { class: "num" }, "Out"),
              h("th", { class: "num" }, "Changes"),
              h("th", { class: "num" }, "Watched"),
            ),
          ),
          h(
            "tbody",
            null,
            ...shown.map((r) => {
              const t = r.stack.type;
              const net = r.in - r.out;
              return h(
                "tr",
                { key: r.key, class: `clickable${favorites.has(r.key) ? " favorite" : ""}`, onclick: () => openResource(this.ctx, r.key, r.stack) },
                h("td", null, h("div", { class: "stack-name" }, favoriteStar(this.ctx, r.key, r.stack), stackIcon(r.stack), stackName(r.stack))),
                h("td", { class: "num" }, amount(r.stock, t)),
                h("td", { class: "num flow-in" }, rate(r.in, r.observedMs, u, t)),
                h("td", { class: "num flow-out" }, rate(r.out, r.observedMs, u, t)),
                h("td", { class: `num ${net < 0 ? "flow-out" : net > 0 ? "flow-in" : ""}` }, rate(net, r.observedMs, u, t, true)),
                h("td", { class: "num", title: full(r.in) }, amount(r.in, t)),
                h("td", { class: "num", title: full(r.out) }, amount(r.out, t)),
                h("td", { class: "num" }, full(r.moves)),
                h("td", { class: "num" }, r.observedMs ? duration(r.observedMs) : "–"),
              );
            }),
          ),
        ),
      ),
      sorted.length > this.shown
        ? h("div", { class: "table-foot" }, h("span", { class: "muted small" }, `${shown.length} of ${sorted.length}`), h("button", { onclick: () => { this.shown += PAGE_SIZE; this.render(); } }, "Show more"))
        : "",
    );
  }
}
